import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, Send, Mail, Loader2 } from "lucide-react";

import { API_URL } from "../../config";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";

const AVAILABLE_FIELDS = [
  { key: "name", label: "Nome do comprador", default: "Nome" },
  { key: "date", label: "Data da sessão", default: "Data" },
  { key: "time", label: "Horário", default: "Horário" },
  { key: "location", label: "Local do evento", default: "Local" },
  { key: "session_subtitle", label: "Subtítulo da sessão", default: "Categoria" },
  { key: "session_name", label: "Nome da sessão", default: "Sessão" },
  { key: "room", label: "Sala", default: "Sala" },
  { key: "ticket_type", label: "Tipo de bilhete", default: "Tipo" },
  { key: "amount_paid", label: "Valor pago", default: "Valor pago" },
  { key: "custom_fields", label: "Campos personalizados do formulário (Igreja, Idade, etc.)", default: null },
];

const DEFAULT_FIELDS = ["name", "date", "time", "location", "custom_fields"];

export const TicketEmailCustomiser = ({ show, setShow, onSave, saving }) => {
  const enabled = Array.isArray(show?.ticket_email_fields) ? show.ticket_email_fields : DEFAULT_FIELDS;
  const labels = show?.ticket_email_field_labels || {};
  const intro = show?.ticket_email_intro_text || "";
  const qrText = show?.ticket_email_qr_text || "";
  const footer = show?.ticket_email_footer_text || "";

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  const toggleField = (key) => {
    const next = enabled.includes(key) ? enabled.filter((k) => k !== key) : [...enabled, key];
    setShow({ ...show, ticket_email_fields: next });
  };

  const setLabel = (key, value) => {
    setShow({ ...show, ticket_email_field_labels: { ...labels, [key]: value } });
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    await refreshPreview();
  };

  const refreshPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/email/ticket-preview`,
        { show: {
          ticket_email_fields: enabled,
          ticket_email_field_labels: labels,
          ticket_email_intro_text: intro,
          ticket_email_qr_text: qrText,
          ticket_email_footer_text: footer,
          accent_color: show?.accent_color,
          name: show?.name,
          location: show?.location,
          hero_badge: show?.hero_badge,
        }},
        { withCredentials: true, responseType: "text" }
      );
      setPreviewHtml(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro a carregar preview");
      setPreviewHtml("<p style='color:red;padding:20px'>Erro a carregar preview</p>");
    } finally { setPreviewLoading(false); }
  };

  // Auto-refresh the preview when the user tweaks fields while it's open.
  React.useEffect(() => {
    if (!previewOpen) return;
    const t = setTimeout(() => { refreshPreview(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled.join(","), JSON.stringify(labels), intro, qrText, footer, previewOpen]);

  const sendTest = async () => {
    if (!testEmail) { toast.error("Insira um email"); return; }
    setSendingTest(true);
    try {
      await axios.post(`${API_URL}/api/admin/email/send-test-ticket`,
        { email: testEmail },
        { withCredentials: true }
      );
      toast.success(`Email de teste enviado para ${testEmail}`);
      setTestOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar");
    } finally { setSendingTest(false); }
  };

  return (
    <div className="mt-6 pt-6 border-t border-white/10" data-testid="ticket-email-customiser">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h4 className="text-white font-semibold">Bilhete por Email — Personalizar</h4>
          <p className="text-zinc-500 text-sm">Escolha os campos visíveis e personalize textos. Pré-visualize antes de aplicar.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={openPreview} variant="outline" className="border-violet-500/30 text-violet-300 hover:bg-violet-500/10" data-testid="btn-preview-ticket">
            <Eye className="w-4 h-4 mr-2" />
            Pré-visualizar
          </Button>
          <Button onClick={() => setTestOpen(true)} variant="outline" className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10" data-testid="btn-send-test-ticket">
            <Send className="w-4 h-4 mr-2" />
            Enviar Teste
          </Button>
        </div>
      </div>

      {/* Field selector */}
      <div className="bg-zinc-950 rounded-lg border border-white/5 p-4 mb-4">
        <p className="text-zinc-300 text-sm font-medium mb-1">Campos a incluir no bilhete</p>
        <p className="text-zinc-500 text-xs mb-3">Pode renomear o título de cada linha (afeta só o email).</p>
        <div className="space-y-2">
          {AVAILABLE_FIELDS.map((f) => {
            const isOn = enabled.includes(f.key);
            return (
              <div key={f.key} className="flex items-center gap-3 hover:bg-white/5 rounded px-2 py-1.5" data-testid={`ticket-field-${f.key}`}>
                <Switch
                  checked={isOn}
                  onCheckedChange={() => toggleField(f.key)}
                  className="data-[state=checked]:bg-emerald-500"
                />
                <span className="text-white text-sm w-56 shrink-0">{f.label}</span>
                {f.default !== null ? (
                  <Input
                    value={labels[f.key] ?? ""}
                    onChange={(e) => setLabel(f.key, e.target.value)}
                    placeholder={f.default}
                    disabled={!isOn}
                    className="input-dark h-8 text-sm flex-1"
                    data-testid={`ticket-field-label-${f.key}`}
                  />
                ) : (
                  <span className="text-zinc-500 text-xs italic flex-1">Usa os labels definidos no formulário</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Custom texts */}
      <div className="space-y-3">
        <div>
          <Label className="text-zinc-300">Texto introdutório <span className="text-zinc-500 text-xs">(aparece antes da tabela)</span></Label>
          <Input
            value={intro}
            onChange={(e) => setShow({ ...show, ticket_email_intro_text: e.target.value })}
            placeholder="Aqui está o seu bilhete oficial. Apresente o QR Code à entrada do evento."
            className="input-dark mt-1.5"
            data-testid="ticket-intro-input"
          />
        </div>
        <div>
          <Label className="text-zinc-300">Texto acima do QR Code</Label>
          <Input
            value={qrText}
            onChange={(e) => setShow({ ...show, ticket_email_qr_text: e.target.value })}
            placeholder="Apresente este QR Code na entrada"
            className="input-dark mt-1.5"
            data-testid="ticket-qr-input"
          />
        </div>
        <div>
          <Label className="text-zinc-300">Texto do rodapé <span className="text-zinc-500 text-xs">(mensagem final)</span></Label>
          <Input
            value={footer}
            onChange={(e) => setShow({ ...show, ticket_email_footer_text: e.target.value })}
            placeholder="Este bilhete é fundamental para a sua entrada no espetáculo..."
            className="input-dark mt-1.5"
            data-testid="ticket-footer-input"
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button onClick={onSave} disabled={saving} className="btn-primary" data-testid="ticket-email-save">
          {saving ? "A guardar..." : "Guardar Personalização"}
        </Button>
      </div>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 sm:max-w-[680px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-violet-300" />
              Pré-visualização do Bilhete
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Usa um bilhete de exemplo. Guarde as alterações para refletir aqui.
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="py-10 flex items-center justify-center text-zinc-400">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              A carregar…
            </div>
          ) : (
            <iframe
              title="Ticket preview"
              srcDoc={previewHtml}
              className="w-full h-[600px] bg-white rounded-lg border border-white/10"
              data-testid="ticket-preview-iframe"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Send test dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-400" />
              Enviar Bilhete de Teste
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Envia uma cópia do bilhete (com dados de exemplo) para o email indicado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-zinc-300">Email de destino</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="seu@email.com"
                className="input-dark mt-1.5"
                data-testid="ticket-test-email"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setTestOpen(false)} className="border-white/10 text-zinc-300">Cancelar</Button>
            <Button onClick={sendTest} disabled={sendingTest} className="btn-primary" data-testid="ticket-test-send">
              {sendingTest ? "A enviar..." : "Enviar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
